import { ContentWrapper, TopBarLogo } from '@collab/atoms';
import { ComponentWithPageMods } from '@collab/layouts/PageLayout';
import { PageTitle } from '@collab/molecules';
import { Icon } from '@volvo-cars/react-icons';
import { TagButton } from 'atoms/TagButton/TagButton';
import { mdxComponents } from 'components/mdxComponents/docMdx';
import { PostMeta } from 'libs/cms/common/types';
import Share from 'molecules/Share';
import { MDXRemote } from 'next-mdx-remote';
import Link from 'next/link';

import Feedback from 'features/feedback/Feedback';
import styles from './Post.module.css';

type NewsPost = Omit<PostMeta, 'featured' | 'href'>;
type PostNavProps = { prevHref?: string; nextHref?: string };

export type NewsPostProps = {
  compiledSource: string;
  frontmatter: NewsPost;
  postNav: PostNavProps;
};

const Post: ComponentWithPageMods<NewsPostProps> = ({
  frontmatter,
  compiledSource,
  postNav,
}) => (
  <>
    <header className={styles.header}>
      <Link
        href="/news"
        className="button-text ml-16 md:ml-32 absolute"
        data-arrow="none"
      >
        <Icon icon="arrow-back" size={16} className="mx-8" aria-hidden />
        <span className="font-14 font-light">Back</span>
      </Link>
      <div className="mx-auto">
        <TopBarLogo />
      </div>
    </header>
    <div className={styles.pageLayout}>
      <main className={styles.innerContent}>
        <>
          <ContentWrapper>
            <div className="stack-16">
              <PageTitle title={frontmatter.title} />
              <p className="text-secondary font-14">{`${frontmatter.date} | ${frontmatter.ttr} min read`}</p>
              <p className="text-secondary font-20">{`${frontmatter.excerpt}`}</p>
              <TagList tags={frontmatter.tags} />
            </div>
          </ContentWrapper>
          <img
            className="img w-full mt-48 aspect-1/1 md:aspect-21/9"
            src={frontmatter.image}
            alt={frontmatter.title}
          />
          <ContentWrapper>
            <div className="mt-48" />
            <MDXRemote
              compiledSource={compiledSource}
              components={mdxComponents}
            />
            <PostNav {...postNav} />
            <div className="mt-l">
              <Share />
            </div>
          </ContentWrapper>
          <aside className="my-m">
            <Feedback />
          </aside>
        </>
      </main>
    </div>
  </>
);

interface TagListProps {
  tags: string[];
}

const TagList: React.FC<TagListProps> = ({ tags }) => (
  <div className="flex flex-wrap justify-left gap-8">
    {tags.map((tag, i) => (
      <TagButton as="span" key={i} className={styles.noPointerEvents}>
        {tag}
      </TagButton>
    ))}
  </div>
);

const PostNav: React.FC<PostNavProps> = ({ prevHref, nextHref }) => (
  <div className="flex justify-between mt-m">
    <Link aria-disabled={!prevHref} href={prevHref ?? '#'}>
      <span aria-disabled={!prevHref} className="button-text" data-arrow="back">
        Previous
      </span>
    </Link>
    <Link aria-disabled={!nextHref} href={nextHref ?? '#'}>
      <span aria-disabled={!nextHref} className="button-text">
        Next
      </span>
    </Link>
  </div>
);

Post.pageMods = { withLayout: false, noHeader: true };

export default Post;
